import React, { Fragment, useMemo, useState } from 'react';
import AppTable from '../../../constants/AppTable';
import { LoadingSpinner } from '../../../constants/AppLoading';
import DialogBox from '../../../constants/DialogBox';
import { Grid } from '@mui/material';
import InputTextBox from '../../../constants/InputTextBox';
import { toleValidationSchema } from '../../../helpers/validation';
import { useFormik } from 'formik';
import { EndBaseLine } from '../../../constants/AppSpace';
import { Button } from '../../../constants/Button';
import { putDataToTheServer } from '../../../helpers/api';
import AppURL from '../../../constants/AppURL';
import { ToastSuccess, ToastWarning } from '../../../constants/Toast';
import DefaultSelectInputBox from '../../../constants/DefaultSelectBox';
import { YesNoOptions } from '../../../constants/Options';

interface TapSize {
    sn: number;
    size_name: string;
    tap_size: string;
    remarks: string;
    active: number;
}

interface MargaToleListViewProps {
    data: {
        results: TapSize[];
        previous: string | null;
        next: string | null;
    } | null;
    loading: boolean;
    error: string | null;
    setOnURL: React.Dispatch<React.SetStateAction<string>>;
    handleFormSuccess: () => void;
}

const TapSizeListView: React.FC<MargaToleListViewProps> = ({ data, loading, error, handleFormSuccess, setOnURL }) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState<TapSize | null>(null);

    const columns = [
        { header: 'SN', accessor: 'sn', sortable: false },
        { header: 'Size Name', accessor: 'size_name', sortable: false },
        { header: 'Tap Size', accessor: 'tap_size', sortable: true },
        { header: 'Remarks', accessor: 'remarks', sortable: false },
        { header: 'Active', accessor: 'active', sortable: true },
        { header: 'Actions', accessor: 'actions', action: true },
    ];

    const modifiedData = useMemo(() => {
        if (!data) return [];
        return data.results.map((item: TapSize) => ({
            sn: item.sn,
            size_name: item.size_name,
            tap_size: item.tap_size,
            remarks: item.remarks,
            active: item.active, // Keep active as a number
        }));
    }, [data]);

    const handleEdit = (item: TapSize) => {
        setCurrentItem(item);
        setDialogOpen(true);
    };

    const formik = useFormik({
        initialValues: {
            size_name: currentItem?.size_name || "",
            tap_size: currentItem?.tap_size || "",
            remarks: currentItem?.remarks || "",
            active: currentItem?.active === 1 ? "1" : "2", // Initialize as a string for form handling
        },
        // Uncomment and use validationSchema as needed
        // validationSchema: toleValidationSchema,
        enableReinitialize: true,
        onSubmit: async (values, action) => {
            const formData = new FormData();
            formData.append("size_name", values.size_name);
            formData.append("tap_size", values.tap_size);
            formData.append("remarks", values.remarks);
            formData.append("active", values.active === "1" ? "1" : "0"); // Convert string to number for API

            try {
                await putDataToTheServer(AppURL.FrontOfficeURL + 'front/tap/size/retrieve/update/view/' + currentItem?.sn, formData).then((res) => {
                    if (res?.status === 200) {
                        handleFormSuccess();
                        setDialogOpen(false);
                        ToastSuccess({ message: res?.message });
                    } else if (res?.status === 400) {
                        ToastWarning({ message: res?.message});
                    } else {
                        ToastWarning({ message: "Some Issue" });
                    }
                });
            } catch (error) {
                console.log(error);
            }
        },
    });

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <LoadingSpinner />
            </div>
        );
    }

    if (error) {
        return <p>Error loading data: {error}</p>;
    }

    return (
        <Fragment>
            <AppTable
                columns={columns}
                data={modifiedData}
                onEdit={handleEdit}
                onNext={() => data?.next && setOnURL(data.next)}
                onPrev={() => data?.previous && setOnURL(data.previous)}
            />
            {dialogOpen && currentItem && (
                <DialogBox open={dialogOpen} headerName='Tole Edit Update' close={() => setDialogOpen(false)}>
                    <form onSubmit={formik.handleSubmit}>
                        <Grid container columnSpacing={3} rowSpacing={0.8}>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                
                                <InputTextBox
                                    type='text'
                                    name='size_name'
                                    label='Size Name'
                                    handleChange={formik.handleChange}
                                    value={formik.values.size_name}
                                    error={formik.errors.size_name}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.size_name}
                                    required={true}
                                    placeholder='Enter size Name'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='tap_size'
                                    label='Tap Size'
                                    handleChange={formik.handleChange}
                                    value={formik.values.tap_size}
                                    error={formik.errors.tap_size}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.tap_size}
                                    required={true}
                                    placeholder='Enter Tap Size'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='remarks'
                                    label='Remarks'
                                    handleChange={formik.handleChange}
                                    value={formik.values.remarks}
                                    error={formik.errors.remarks}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.remarks}
                                    required={false}
                                    placeholder='Remarks'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <DefaultSelectInputBox
                                    name="active"
                                    label='Active'
                                    disabled={false} // Adjust based on your needs
                                    options={YesNoOptions}
                                    value={formik.values.active}
                                    optionSelect={(name, selectedOption) => {
                                        formik.setFieldValue(name, selectedOption.value);
                                    }}
                                    clear={true}
                                    placeholder="Select Options"
                                />
                            </Grid>

                            <Grid item xl={12} lg={12} sm={12} md={12} xs={12}>
                                <EndBaseLine top={18}>
                                    <Button btn_type='save' type='submit' el='button' />
                                </EndBaseLine>
                            </Grid>
                        </Grid>
                    </form>
                </DialogBox>
            )}
        </Fragment>
    );
};

export default TapSizeListView;

